import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const links = [
  { labelKey: "nav.services", hash: "#services" },
  { labelKey: "nav.whyus", hash: "#why-us" },
  { labelKey: "nav.environment", hash: "#environment" },
  { labelKey: "nav.contact", hash: "#contact" },
];

function LanguageToggle() {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center border border-white/15 rounded-full overflow-hidden" data-testid="language-toggle">
      {["en", "pt"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
            lang === l ? "bg-[#FF5A00] text-black" : "text-neutral-400 hover:text-[#FF5A00]"
          }`}
          data-testid={`lang-toggle-${l}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export const scrollToHash = (hash) => (e) => {
  e.preventDefault();
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (hash) => (e) => {
    setMenuOpen(false);
    scrollToHash(hash)(e);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || menuOpen ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <a href="#top" onClick={handleNav("#top")} className="flex items-center" data-testid="nav-logo">
          <img src="/fenrion-logo.png" alt="Fenrion AI" className="h-11 w-auto mix-blend-screen" data-testid="nav-logo-img" />
        </a>
        <nav className="hidden md:flex items-center gap-10" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              onClick={handleNav(l.hash)}
              className="text-sm text-neutral-400 hover:text-[#FF5A00] transition-colors duration-300 tracking-wide"
              data-testid={`nav-link-${l.hash.slice(1)}`}
            >
              {t(l.labelKey)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <a
            href="#contact"
            onClick={handleNav("#contact")}
            className="group hidden sm:flex items-center gap-2 bg-[#FF5A00] text-black text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#FFB800] transition-colors duration-300"
            data-testid="nav-cta-button"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden w-11 h-11 flex items-center justify-center border border-white/15 rounded-full text-neutral-200 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors duration-300"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  onClick={handleNav(l.hash)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.3 }}
                  className="font-syne font-bold text-2xl py-3 border-b border-white/5 text-neutral-200 hover:text-[#FF5A00] transition-colors duration-300"
                  data-testid={`nav-mobile-link-${l.hash.slice(1)}`}
                >
                  {t(l.labelKey)}
                </motion.a>
              ))}
              <div className="mt-4 flex items-center gap-3">
                <a
                  href="#contact"
                  onClick={handleNav("#contact")}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#FF5A00] text-black font-medium px-6 py-3.5 rounded-full"
                  data-testid="nav-mobile-cta"
                >
                  {t("nav.cta")}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <LanguageToggle />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
